/*
 * ----------------------------------------------------------------------------
 * OFFSET CART DRAWER
 * ----------------------------------------------------------------------------
 *
 * Behaviour for sections/cart-drawer.liquid. Two jobs:
 *
 *   1. The minus / plus buttons on each line.
 *   2. The "+ Add" buttons on the essentials cards.
 *
 * Every listener is delegated from the document. That matters: the theme
 * rebuilds the drawer's markup by replacing innerHTML on every cart change, so
 * anything bound to an element inside it would be thrown away after the first
 * interaction.
 *
 * The quantity work is deliberately thin. The theme's own <line-item-quantity>
 * element already talks to /cart/change.js and re-renders everything; all these
 * buttons do is set the number and fire the change event it is already
 * listening for. Reimplementing that would mean two code paths that have to
 * agree about discounts, quantity rules and error states.
 */
(function () {
  'use strict';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------------------ quantity -- */

  function stepQuantity(button) {
    var wrap = button.closest('line-item-quantity');
    var input = wrap && wrap.querySelector('input.quantity-input');
    if (!input) return;

    var step = parseInt(button.getAttribute('data-ocd-step'), 10) || 0;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 1;

    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;

    var max = parseInt(input.getAttribute('max'), 10);
    var next = current + step;

    // Stepping below the minimum means they want the line gone. Hand that to
    // the remove link so it goes through the same path as clicking Remove,
    // rather than posting a quantity the shop's rules would reject.
    if (next < min) {
      var remove = wrap.querySelector('[data-ocd-remove]');
      if (remove) remove.click();
      return;
    }

    if (!isNaN(max) && next > max) return;
    if (next === current) return;

    input.value = String(next);

    // <line-item-quantity> listens for change and posts to /cart/change.js.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------- essentials -- */

  function addEssential(button) {
    var variantId = button.getAttribute('data-ocd-add');
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');

    // Ask the cart drawer (and anything else listening) which sections it needs
    // re-rendered, exactly the way the theme's own product form does. Without
    // this the drawer would update its count but not its contents.
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections })
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw new Error(json.description || 'add failed');
          return json;
        });
      })
      .then(function (added) {
        // Show the tick straight away. The re-render below wipes this button
        // out anyway, but on a slow connection the shopper sees it land.
        button.setAttribute('data-done', '');
        button.removeAttribute('aria-busy');

        // /cart/add.js returns the added lines, not the whole cart, and the
        // drawer needs a full cart object to redraw its totals.
        return fetch(root() + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            cart.sections = added.sections;
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cart }
              })
            );
          });
      })
      .catch(function () {
        // Put the control back rather than leaving a dead button behind.
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-done');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
  }

  /* ------------------------------------------------- essentials slider -- */

  function essParts() {
    var track = document.querySelector('#cart-drawer [data-ocd-ess-track]');
    if (!track) return null;
    var wrap = track.closest('[data-ocd-ess]');
    return {
      track: track,
      wrap: wrap,
      prev: wrap && wrap.querySelector('[data-ocd-ess-prev]'),
      next: wrap && wrap.querySelector('[data-ocd-ess-next]')
    };
  }

  /*
   * Decides whether the arrows are worth showing and which of them are live.
   * Has to be re-run after every cart change, because the drawer replaces this
   * markup wholesale and a card can drop out of the row once it is in the cart.
   */
  function refreshSlider() {
    var p = essParts();
    if (!p) return;

    var maxScroll = p.track.scrollWidth - p.track.clientWidth;
    var scrollable = maxScroll > 2;

    if (p.wrap) p.wrap.classList.toggle('ocd__ess--scrollable', scrollable);
    if (p.prev) p.prev.disabled = !scrollable || p.track.scrollLeft <= 2;
    if (p.next) p.next.disabled = !scrollable || p.track.scrollLeft >= maxScroll - 2;

    // scroll does not bubble, so it is bound to the track itself. The flag
    // keeps a fresh listener off a track that already has one.
    if (!p.track.__ocdBound) {
      p.track.__ocdBound = true;
      p.track.addEventListener('scroll', refreshSlider, { passive: true });
    }
  }

  function slide(direction) {
    var p = essParts();
    if (!p) return;
    var card = p.track.querySelector('.ocd__ess-card');
    var gap = 10;
    var step = card ? card.getBoundingClientRect().width + gap : p.track.clientWidth * 0.8;
    p.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  /* ------------------------------------------------------------- binding -- */

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    if (!event.target.closest('#cart-drawer')) return;

    var step = event.target.closest('[data-ocd-step]');
    if (step) {
      event.preventDefault();
      stepQuantity(step);
      return;
    }

    var add = event.target.closest('[data-ocd-add]');
    if (add && !add.hasAttribute('aria-busy') && !add.hasAttribute('data-done')) {
      event.preventDefault();
      addEssential(add);
      return;
    }

    if (event.target.closest('[data-ocd-ess-prev]')) {
      event.preventDefault();
      slide(-1);
      return;
    }

    if (event.target.closest('[data-ocd-ess-next]')) {
      event.preventDefault();
      slide(1);
    }
  });

  /* The theme dims a line while its change is in flight. It sets this on the
     <line-item> element, so the drawer's own styling can pick it up. */
  document.addEventListener('line-item:will-change', function (event) {
    var item = event.target.closest ? event.target.closest('line-item') : null;
    if (item && item.closest('#cart-drawer')) item.setAttribute('aria-busy', 'true');
  });

  /*
   * A rejected quantity change leaves the drawer lying about the cart.
   *
   * Ask for 999 of something with 150 in stock and Shopify puts 150 in the
   * cart and returns an error. The theme catches the error and resets the box
   * to its previous value, so the shopper is looking at "1" while the cart
   * holds 150. They find out at checkout.
   *
   * So: put the true number back straight away, keeping the theme's error
   * message on screen, then refresh everything else once they have had a few
   * seconds to read it.
   */
  document.addEventListener('line-item:error', function (event) {
    var item = event.target.closest ? event.target.closest('line-item') : null;
    if (!item) return;

    item.removeAttribute('aria-busy');
    if (!item.closest('#cart-drawer')) return;

    var input = item.querySelector('input.quantity-input');
    var key = input && input.getAttribute('data-line-key');
    if (!key) return;

    fetch(root() + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (i) { return i.key === key; })[0];
        var real = line ? line.quantity : 0;

        input.value = String(real);
        input.defaultValue = String(real);

        // The count bubble and the subtotal are stale too. Give the message a
        // few seconds on screen before the drawer redraws and clears it.
        window.setTimeout(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }, 4000);
      })
      .catch(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
  });

  /* ------------------------------------------------------------ slider up -- */

  /*
   * The drawer's markup is swapped out by the theme, not by us, and there is no
   * event that fires once that is done. Watching for the child list changing
   * catches every one of them: a quantity step, a removal, an add from
   * anywhere on the site, and the first render after the cart drawer is
   * fetched fresh from the server.
   */
  function watchDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    new MutationObserver(refreshSlider).observe(drawer, { childList: true, subtree: true });
    refreshSlider();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }

  // Opening the drawer is when the track first gets a real width to measure.
  document.addEventListener('dialog:after-show', refreshSlider);
  window.addEventListener('resize', refreshSlider);
})();
